import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { paymentRecoveryService } from '../../../lib/paymentRecoveryService';

const FailedPaymentsDashboard = ({ failedPayments, onRefresh }) => {
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [filterStage, setFilterStage] = useState('all');
  const [sortBy, setSortBy] = useState('date');

  const dunningStages = [
    { value: 'all', label: 'All Stages', color: 'gray' },
    { value: 'initial', label: 'Initial', color: 'blue' },
    { value: 'reminder_1', label: 'Reminder 1', color: 'yellow' },
    { value: 'reminder_2', label: 'Reminder 2', color: 'orange' },
    { value: 'final_notice', label: 'Final Notice', color: 'red' },
    { value: 'suspended', label: 'Suspended', color: 'gray' }
  ];

  const filteredPayments = failedPayments?.filter(payment => 
    filterStage === 'all' || payment?.dunning_stage === filterStage
  );

  const sortedPayments = [...(filteredPayments || [])]?.sort((a, b) => {
    if (sortBy === 'date') {
      return new Date(b?.created_at) - new Date(a?.created_at);
    } else if (sortBy === 'amount') {
      return parseFloat(b?.amount) - parseFloat(a?.amount);
    } else if (sortBy === 'priority') {
      return b?.member_value_score - a?.member_value_score;
    }
    return 0;
  });

  const getPriorityColor = (memberValueScore) => {
    if (memberValueScore >= 8) return { bg: 'bg-red-100', text: 'text-red-800', label: 'High Priority' };
    if (memberValueScore >= 5) return { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Medium Priority' };
    return { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Low Priority' };
  };

  const getStageColor = (stage) => {
    const stageConfig = dunningStages?.find(s => s?.value === stage);
    return stageConfig?.color || 'gray';
  };

  const handleManualResolve = async (paymentId) => {
    if (!confirm('Mark this payment as manually resolved?')) return;

    const { error } = await paymentRecoveryService?.resolvePaymentManually(
      paymentId,
      'manual_resolution',
      'Resolved by staff member'
    );

    if (!error) {
      alert('Payment marked as resolved');
      onRefresh?.();
    } else {
      alert('Failed to resolve payment');
    }
  };

  const handleRetryNow = async (paymentId) => {
    if (!confirm('Trigger immediate payment retry?')) return;

    const { data, error } = await paymentRecoveryService?.schedulePaymentRetry(paymentId, 1);

    if (!error) {
      alert('Payment retry scheduled');
      onRefresh?.();
    } else {
      alert('Failed to schedule retry');
    }
  };

  return (
    <div className="space-y-6">
      {/* Filters and Sort */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Filter by Stage</label>
            <select
              value={filterStage}
              onChange={(e) => setFilterStage(e?.target?.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              {dunningStages?.map(stage => (
                <option key={stage?.value} value={stage?.value}>{stage?.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Sort By</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e?.target?.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="date">Date (Newest)</option>
              <option value="amount">Amount (Highest)</option>
              <option value="priority">Priority (Highest)</option>
            </select>
          </div>
        </div>
        <div className="text-sm text-gray-600">
          Showing {sortedPayments?.length} failed payments
        </div>
      </div>

      {/* Failed Payments List */}
      <div className="space-y-4">
        {sortedPayments?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="CheckCircle" className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Failed Payments</h3>
            <p className="text-gray-600">All payments are processing successfully</p>
          </div>
        ) : (
          sortedPayments?.map((payment) => {
            const priority = getPriorityColor(payment?.member_value_score);
            const stageColor = getStageColor(payment?.dunning_stage);

            return (
              <div
                key={payment?.id}
                className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {payment?.user?.full_name || 'Unknown Member'}
                      </h3>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${priority?.bg} ${priority?.text}`}>
                        {priority?.label}
                      </span>
                      <span className={`px-2 py-1 rounded text-xs font-medium bg-${stageColor}-100 text-${stageColor}-800`}>
                        {payment?.dunning_stage?.replace('_', ' ')?.toUpperCase()}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600">Amount</p>
                        <p className="font-semibold text-gray-900">${payment?.amount}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Plan</p>
                        <p className="font-semibold text-gray-900 capitalize">{payment?.subscription_plan}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Retry Count</p>
                        <p className="font-semibold text-gray-900">{payment?.retry_count} / {payment?.max_retry_attempts}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Next Retry</p>
                        <p className="font-semibold text-gray-900">
                          {payment?.next_retry_date 
                            ? new Date(payment?.next_retry_date)?.toLocaleDateString()
                            : 'Not scheduled'
                          }
                        </p>
                      </div>
                    </div>

                    <div className="mt-3 p-3 bg-red-50 rounded-lg">
                      <div className="flex items-start gap-2">
                        <Icon name="AlertCircle" className="w-4 h-4 text-red-600 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-red-900">{payment?.failure_reason}</p>
                          <p className="text-xs text-red-700 mt-1">{payment?.failure_message}</p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-3 flex items-center gap-2 text-xs text-gray-600">
                      <Icon name="Mail" size={14} />
                      <span>{payment?.user?.email}</span>
                      {payment?.user?.phone && (
                        <>
                          <span className="mx-2">•</span>
                          <Icon name="Phone" size={14} />
                          <span>{payment?.user?.phone}</span>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 ml-4">
                    <Button
                      size="sm"
                      iconName="RefreshCw"
                      onClick={() => handleRetryNow(payment?.id)}
                    >
                      Retry Now
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      iconName="CheckCircle"
                      onClick={() => handleManualResolve(payment?.id)}
                    >
                      Mark Resolved
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      iconName="MessageSquare"
                      onClick={() => setSelectedPayment(payment)}
                    >
                      Contact
                    </Button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default FailedPaymentsDashboard;